import { useEffect, useState } from "react";
import StrassenFeld from "./StrassenFeld";

import { useCreateKunde, useKunden } from "../../hooks/usePlanung";
import { errorText } from "../../lib/invoices";
import {
  adressPruefung, aehnlicheKunden, hatName, kundeBodyAusWerten, kundentypWechseln, leereKundeWerte,
  type Kommunikationskanal, type KundeWerte,
} from "../../lib/kundeAnlage";
import type { Kunde } from "../../lib/types";
import AnredeFeld from "./AnredeFeld";
import { namenTeilen } from "../../lib/anrede";
import { AnsprechpartnerFeld, KontaktwegeFeld } from "./KontaktListen";

/**
 * Neuen Kunden anlegen, ohne den Beleg zu verlassen.
 *
 * Bis hierher musste man für einen neuen Kunden zur Kundenseite, ihn dort
 * anlegen, zurück zum Entwurf und ihn suchen (Benjamin, 02.09.2026). Der
 * Dialog fragt dieselben Felder wie die Kundenseite ab — nur die
 * Pflegemonate nicht, die gehören zur Pflegeplanung, nicht zum Beleg.
 *
 * Stammdatenqualität (Benjamin, 13.10.2026: „Die Stammdaten müssen konsistent und
 * sauber in der Kundendatenbank landen — vor allem die korrekte Adresse"):
 *   * Die Anschrift kommt aus der Adresssuche (Straße, PLZ, Ort getrennt). Eine
 *     unvollständige wird nicht gespeichert; ohne Anschrift oder ohne Hausnummer
 *     fragt der Dialog einmal nach.
 *   * Gibt es den Kunden vermutlich schon (Mail, Rufnummer, Name), steht er oben —
 *     mit „Diesen nehmen" statt einer Dublette.
 */

type Props = {
  /** Was im Suchfeld stand — wird als Name vorgeschlagen. */
  vorschlagName?: string;
  /* Wozu der Kunde gleich gebraucht wird. Der Dialog steht inzwischen an
     mehreren Stellen (Belegeditor, Planer, Projektanlage) — ein fester Satz
     über „diesen Beleg" wäre an dreien davon schlicht falsch. */
  hinweis?: string;
  onAngelegt: (kunde: Kunde) => void;
  onSchliessen: () => void;
};

export default function KundeNeuDialog({
  vorschlagName = "",
  hinweis = "landet in der Kundenliste und wird Empfänger dieses Belegs",
  onAngelegt,
  onSchliessen,
}: Props) {
  const anlegen = useCreateKunde();
  const { data: kunden = [] } = useKunden();
  const [form, setForm] = useState<KundeWerte>(() => ({
    ...leereKundeWerte(), name: vorschlagName.trim(), ...namenTeilen(vorschlagName),
  }));
  const [rechnungOffen, setRechnungOffen] = useState(false);
  const [fehler, setFehler] = useState("");
  /** Die Rückfrage, die schon mit „trotzdem" beantwortet ist — ein zweiter Tipp legt an. */
  const [bestaetigt, setBestaetigt] = useState("");
  const vorhandene = aehnlicheKunden(form, kunden);

  const setF = <K extends keyof KundeWerte>(key: K, value: KundeWerte[K]) => (
    setForm((f) => ({ ...f, [key]: value }))
  );

  useEffect(() => {
    const aufTaste = (event: KeyboardEvent) => { if (event.key === "Escape") onSchliessen(); };
    document.addEventListener("keydown", aufTaste);
    return () => document.removeEventListener("keydown", aufTaste);
  }, [onSchliessen]);

  const speichern = (event: React.FormEvent) => {
    event.preventDefault();
    if (!hatName(form)) {
      setFehler(form.kundentyp === "privat" ? "Vor- oder Nachname fehlt." : "Der Firmenname fehlt.");
      return;
    }
    const adr = adressPruefung(form.strasse, form.plz, form.ort);
    if (adr.fehler) { setFehler(adr.fehler); return; }
    if (rechnungOffen) {
      const re = adressPruefung(form.rechnung_strasse, form.rechnung_plz, form.rechnung_ort);
      if (re.fehler) { setFehler(`Rechnungsadresse: ${re.fehler}`); return; }
    }
    const rueckfrage = [
      vorhandene.length ? `Es gibt schon ${vorhandene.map((k) => k.name).join(", ")}.` : "",
      adr.leer ? "Ohne Anschrift — Planer, Fotos und Rechnung brauchen sie." : adr.hinweis,
    ].filter(Boolean).join(" ");
    if (rueckfrage && rueckfrage !== bestaetigt) {
      setBestaetigt(rueckfrage);
      setFehler(`${rueckfrage} Nochmal „Kunde anlegen" legt trotzdem an.`);
      return;
    }
    setFehler("");
    anlegen.mutate(kundeBodyAusWerten(form), {
      onSuccess: (kunde) => onAngelegt(kunde),
      onError: (error) => setFehler(errorText(error)),
    });
  };

  return (
    <div className="invoice-pflege-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onSchliessen(); }}>
      <form className="invoice-pflege-dialog kunde-neu-dialog" role="dialog" aria-modal="true" aria-label="Neuen Kunden anlegen" onSubmit={speichern}>
        <div className="invoice-titel-pflege-kopf">
          <span>Neuer Kunde</span>
          <small>{hinweis}</small>
        </div>

        {vorhandene.length > 0 && (
          <div className="kunde-neu-vorhanden" role="note">
            <span>Gibt es schon?</span>
            {vorhandene.map((k) => (
              <button key={k.id} type="button" className="invoice-button" onClick={() => onAngelegt(k)}
                title="Diesen Kunden nehmen statt einen neuen anzulegen">
                {k.name}{k.ort ? ` · ${k.ort}` : ""} — diesen nehmen
              </button>
            ))}
          </div>
        )}

        <div className="kunde-neu-typ" role="radiogroup" aria-label="Kundentyp">
          <label>
            <input type="radio" checked={form.kundentyp === "privat"} onChange={() => setForm((f) => kundentypWechseln(f, "privat"))} />
            Privatkunde
          </label>
          <label>
            <input type="radio" checked={form.kundentyp === "firma"} onChange={() => setForm((f) => kundentypWechseln(f, "firma"))} />
            Firmenkunde
          </label>
        </div>

        <div className="kunde-neu-felder">
          {form.kundentyp === "firma" ? (
            <label className="ist-breit">
              <span>Firmenname *</span>
              <input value={form.name} onChange={(e) => setF("name", e.target.value)} autoFocus />
            </label>
          ) : (
            <div className="ist-breit">
              <AnredeFeld
                anrede={form.anrede} vorname={form.vorname} nachname={form.nachname} autoFocus
                onChange={(w) => setForm((f) => ({ ...f, ...w }))}
              />
            </div>
          )}
          <label><span>E-Mail</span><input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} /></label>
          <label><span>Telefon</span><input type="tel" value={form.phone} onChange={(e) => setF("phone", e.target.value)} /></label>
          <label>
            <span>Bevorzugter Kontakt</span>
            <select value={form.kommunikationskanal} onChange={(e) => setF("kommunikationskanal", e.target.value as Kommunikationskanal)}>
              <option value="">Keine Angabe</option>
              <option value="telefon">Telefon</option>
              <option value="mail">E-Mail</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="telegram">Telegram</option>
            </select>
          </label>
          <label><span>Website</span><input type="url" placeholder="https://…" value={form.website} onChange={(e) => setF("website", e.target.value)} /></label>
        </div>

        <div className="kunde-neu-abschnitt">
          <KontaktwegeFeld kontakte={form.kontakte} onChange={(kontakte) => setF("kontakte", kontakte)} />
        </div>

        {form.kundentyp === "firma" && (
          <div className="kunde-neu-abschnitt">
            <AnsprechpartnerFeld kontakte={form.kontakte} onChange={(kontakte) => setF("kontakte", kontakte)} />
          </div>
        )}

        <div className="kunde-neu-abschnitt">
          <div className="kunde-neu-abschnitt-kopf"><span>Adresse — gilt für Lieferung und Rechnung</span><small>Straße tippen und einen Vorschlag wählen</small></div>
          <div className="kunde-neu-felder">
            <label className="ist-breit">
              <span>Straße</span>
              <StrassenFeld
                value={form.strasse}
                placeholder="Adresse suchen — Straße und Hausnummer"
                onChange={(v) => setF("strasse", v)}
                onPick={(teile) => {
                  if (teile.plz) setF("plz", teile.plz);
                  if (teile.ort) setF("ort", teile.ort);
                }}
              />
            </label>
            <label className="ist-plz"><span>PLZ</span><input value={form.plz} inputMode="numeric" maxLength={5} onChange={(e) => setF("plz", e.target.value)} /></label>
            <label className="ist-ort"><span>Ort</span><input value={form.ort} onChange={(e) => setF("ort", e.target.value)} /></label>
          </div>
          <button type="button" className="invoice-linkbutton kunde-neu-klapp" onClick={() => setRechnungOffen((o) => !o)} aria-expanded={rechnungOffen}>
            {rechnungOffen ? "▾" : "▸"} Abweichende Rechnungsadresse
          </button>
          {rechnungOffen && (
            <div className="kunde-neu-felder">
              <label className="ist-breit"><span>Straße</span>
                <StrassenFeld
                  value={form.rechnung_strasse}
                  onChange={(v) => setF("rechnung_strasse", v)}
                  onPick={(teile) => {
                    if (teile.plz) setF("rechnung_plz", teile.plz);
                    if (teile.ort) setF("rechnung_ort", teile.ort);
                  }}
                />
              </label>
              <label className="ist-plz"><span>PLZ</span><input value={form.rechnung_plz} onChange={(e) => setF("rechnung_plz", e.target.value)} /></label>
              <label className="ist-ort"><span>Ort</span><input value={form.rechnung_ort} onChange={(e) => setF("rechnung_ort", e.target.value)} /></label>
            </div>
          )}
        </div>

        <div className="kunde-neu-felder">
          <label className="ist-breit"><span>Notizen</span><textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></label>
        </div>

        {fehler && <p className="invoice-titel-pflege-fehler" role="alert">{fehler}</p>}
        <div className="invoice-titel-pflege-aktionen">
          <button type="button" className="invoice-button" onClick={onSchliessen}>Abbrechen</button>
          <button type="submit" className="invoice-button primary" disabled={anlegen.isPending}>
            {anlegen.isPending ? "Legt an …" : "Kunde anlegen und übernehmen"}
          </button>
        </div>
      </form>
    </div>
  );
}
